import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface UploadBatchProgress {
  batchId: string;
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  progress: number; // 0–100, monotonically increasing
  isComplete: boolean;
  hasErrors: boolean;
  batchStatus: string; // 'queued' | 'processing' | 'completed' | 'partial' | 'failed'
  isInitializing: boolean;
}

export function useUploadProgress(batchId: string | null) {
  const [batchData, setBatchData] = useState<{
    total_jobs: number;
    done_jobs: number;
    uploaded_jobs: number;
    failed_jobs: number;
    status: string;
  } | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [smoothProgress, setSmoothProgress] = useState(0);
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Initial fetch when batchId is set
  useEffect(() => {
    if (!batchId) {
      setBatchData(null);
      setSmoothProgress(0);
      return;
    }

    const fetchBatch = async () => {
      const { data, error } = await supabase
        .from('creative_upload_batches')
        .select('total_jobs, done_jobs, uploaded_jobs, failed_jobs, status')
        .eq('id', batchId)
        .single();

      if (!error && data) {
        setBatchData({
          total_jobs: Number(data.total_jobs) || 0,
          done_jobs: Number(data.done_jobs) || 0,
          uploaded_jobs: Number(data.uploaded_jobs) || 0,
          failed_jobs: Number(data.failed_jobs) || 0,
          status: String(data.status || 'processing'),
        });
        const total = Number(data.total_jobs) || 0;
        const done = Number(data.done_jobs) || 0;
        if (total > 0) {
          setSmoothProgress(prev => Math.max(prev, Math.round((done / total) * 100)));
        }
      }
    };

    fetchBatch();
  }, [batchId]);

  // Realtime subscription on creative_upload_batches
  useEffect(() => {
    if (!batchId) return;

    // Guard: don't subscribe if no authenticated user
    let cancelled = false;
    const setup = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || cancelled) return;

      const channel = supabase
        .channel(`batch-progress-${batchId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'creative_upload_batches',
            filter: `id=eq.${batchId}`,
          },
          (payload) => {
            const row = payload.new as any;
            setBatchData({
              total_jobs: Number(row.total_jobs) || 0,
              done_jobs: Number(row.done_jobs) || 0,
              uploaded_jobs: Number(row.uploaded_jobs) || 0,
              failed_jobs: Number(row.failed_jobs) || 0,
              status: String(row.status || 'processing'),
            });

            const total = Number(row.total_jobs) || 0;
            const done = Number(row.done_jobs) || 0;
            if (total > 0) {
              const newPct = Math.round((done / total) * 100);
              setSmoothProgress(prev => Math.max(prev, newPct));
            }
          }
        )
        .subscribe((status) => {
          setIsSubscribed(status === 'SUBSCRIBED');
        });

      channelRef.current = channel;
    };

    setup();

    return () => {
      cancelled = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setIsSubscribed(false);
    };
  }, [batchId]);

  // Clean up all channels on sign-out
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
        setBatchData(null);
        setSmoothProgress(0);
        setIsSubscribed(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const currentStatus = batchData?.status ?? '';
  const totalJobs = batchData?.total_jobs ?? 0;
  const isTerminal = currentStatus === 'completed' || currentStatus === 'partial' || currentStatus === 'failed';
  const hasErrors = (batchData?.failed_jobs ?? 0) > 0 || currentStatus === 'failed';
  const isInitializing = currentStatus === 'processing' && totalJobs === 0;

  const progress: UploadBatchProgress | null = batchId && batchData
    ? {
        batchId,
        totalFiles: batchData.total_jobs,
        completedFiles: batchData.uploaded_jobs,
        failedFiles: batchData.failed_jobs,
        progress: smoothProgress,
        isComplete: isTerminal || (batchData.total_jobs > 0 && batchData.done_jobs >= batchData.total_jobs),
        hasErrors,
        batchStatus: batchData.status,
        isInitializing,
      }
    : null;

  const reset = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    setBatchData(null);
    setSmoothProgress(0);
    setIsSubscribed(false);
  }, []);

  return { progress, isSubscribed, reset };
}
